import { Download, FileText, ExternalLink } from "lucide-react";
import { SITE, CREDITS } from "../data/site";
import useReveal from "../hooks/useReveal";

export default function Resume() {
  const ref = useReveal();

  return (
    <section
      id="resume"
      data-testid="resume-section"
      className="relative py-28 sm:py-36 lg:py-44 bg-[#0A0A0C] grain"
    >
      <div ref={ref} className="reveal max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline text-[#991B1B] mb-6">— Resume</div>
            <h2
              data-testid="resume-title"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[0.95]"
            >
              The full <span className="italic text-[#A0A0A8]">page.</span>
            </h2>
            <p className="mt-8 text-[#A0A0A8] text-base leading-relaxed max-w-md">
              The complete acting résumé — credits, training, awards, and references — available as a downloadable PDF.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                data-testid="resume-download-btn"
                href={SITE.resumePdf}
                download="Isaiah-Artavia-Acting-Resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cine"
              >
                <Download size={14} />
                Download PDF Resume
              </a>
              <a
                data-testid="resume-view-btn"
                href={SITE.resumePdf}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
              >
                <FileText size={14} />
                View in Browser
                <ExternalLink size={12} />
              </a>
            </div>

            <div className="mt-12 space-y-2 text-xs text-[#6B6B72]">
              <div>{SITE.contact.address}</div>
              <div>{SITE.contact.phone} · {SITE.contact.email}</div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-white/10 bg-[#121215] p-8 sm:p-10">
              <div className="flex items-baseline justify-between border-b border-white/10 pb-6 mb-6 flex-wrap gap-3">
                <div>
                  <div className="overline text-[#D4AF37]">Résumé · Preview</div>
                  <div className="font-serif-display text-3xl text-white mt-1">Isaiah Artavia</div>
                </div>
                <div className="text-right text-xs text-[#6B6B72]">
                  <div>{SITE.contact.location}</div>
                  <div className="mt-1">{SITE.contact.email}</div>
                </div>
              </div>

              <div className="space-y-7">
                <Block label="Recent Theatre" items={CREDITS.theatre.slice(0, 6)} />
                <Block label="Film & Screen" items={CREDITS.film} />
                <Block label="Training" items={CREDITS.training} />

                <div>
                  <div className="overline mb-3">Major Awards</div>
                  <div className="space-y-2">
                    {CREDITS.awards.map((a, i) => (
                      <div key={i} className="grid grid-cols-12 gap-3 text-sm">
                        <div className="col-span-3 text-[#D4AF37]">{a.year}</div>
                        <div className="col-span-9 text-white">
                          <span className="italic font-serif-display text-base">{a.title}</span>
                          <span className="block text-[#A0A0A8] text-xs mt-0.5">{a.detail}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-5 mt-7 text-[11px] text-[#6B6B72] uppercase tracking-[0.2em]">
                A preview — download the PDF for the complete résumé and references
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Block({ label, items }) {
  return (
    <div>
      <div className="overline mb-3">{label}</div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-3 text-sm">
            <div className="col-span-2 text-[#D4AF37]">{it.year}</div>
            <div className="col-span-6 text-white italic font-serif-display text-base">{it.project}</div>
            <div className="col-span-4 text-[#A0A0A8] text-right">{it.role}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
